import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StatusBadge from '../components/StatusBadge';
import { ErrorBanner } from '../components/Feedback';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

const EMPTY_PAGE = { items: [], page: 0, totalItems: 0, hasNext: false };

/**
 * The tabs each role works from; Sales decides on what is routed to them, and the back
 * office sees the whole queue. Support opens on "To review" because triage is where
 * their day starts, and the tabs after it are the admin's, in the admin's order.
 */
const TABS = {
  SALES_REP: [
    { label: 'Awaiting my decision', statuses: ['ASSIGNED_TO_SALES'] },
    { label: 'Verified', statuses: ['VERIFIED'] },
    { label: 'Rejected', statuses: ['REJECTED'] },
  ],
  SUPER_ADMIN: [
    { label: 'To review', statuses: ['PENDING', 'UNDER_REVIEW'] },
    { label: 'With sales', statuses: ['ASSIGNED_TO_SALES'] },
    { label: 'Verified', statuses: ['VERIFIED'] },
    { label: 'Rejected', statuses: ['REJECTED'] },
    { label: 'Suspended', statuses: ['SUSPENDED', 'EXPIRED'] },
    { label: 'All', statuses: [] },
  ],
};

TABS.SUPPORT_EXECUTIVE = TABS.SUPER_ADMIN;

export default function VerificationQueuePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const tabs = TABS[user?.role] ?? TABS.SUPER_ADMIN;

  const [tabIndex, setTabIndex] = useState(0);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(EMPTY_PAGE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const statuses = tabs[tabIndex]?.statuses ?? [];

  const load = useCallback(
    async (pageNumber = 0) => {
      setLoading(true);
      try {
        setPage(await api.verificationQueue({ status: statuses, query, page: pageNumber, size: 20 }));
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    // statuses is a fresh array each render, so depend on its contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statuses.join(','), query],
  );

  useEffect(() => {
    const handle = setTimeout(() => load(0), 250);
    return () => clearTimeout(handle);
  }, [load]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Teacher Verification</h1>
          <p>
            {user?.role === 'SALES_REP'
              ? 'Teachers routed to you for a final decision.'
              : 'Teachers waiting on document review before their resources unlock.'}
          </p>
        </div>
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      <section className="card card-pad">
        <div className="toolbar">
          <div className="seg" role="tablist" aria-label="Queue">
            {tabs.map((tab, index) => (
              <label className="seg-opt" key={tab.label}>
                <input
                  type="radio"
                  name="queue-tab"
                  checked={index === tabIndex}
                  onChange={() => setTabIndex(index)}
                />
                <span>{tab.label}</span>
              </label>
            ))}
          </div>
          <input
            className="input"
            style={{ flex: '1 1 200px' }}
            placeholder="Search name, email, mobile or school"
            aria-label="Search teachers"
            maxLength={100}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
      </section>

      <section className="card">
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Teacher</th>
                <th>School</th>
                <th>Designation</th>
                <th>Status</th>
                <th>ID proof</th>
                <th>Sales rep</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {page.items.map((row) => (
                <tr key={row.teacherUserId}>
                  <td>
                    <div className="cell-user">
                      <div>
                        {row.fullName}
                        <span>{row.email ?? row.mobile}</span>
                      </div>
                    </div>
                  </td>
                  <td>{row.schoolName ?? <span className="muted">—</span>}</td>
                  <td>{row.designation ?? <span className="muted">—</span>}</td>
                  <td>
                    <StatusBadge status={row.status} />
                  </td>
                  <td>
                    {row.idProofUploaded ? (
                      <span className="badge badge-ok">Uploaded</span>
                    ) : (
                      <span className="badge badge-warn">Missing</span>
                    )}
                  </td>
                  <td>{row.assignedSalesRep?.fullName ?? <span className="muted">—</span>}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => navigate(`/verification/${row.teacherUserId}`)}
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && <div className="empty">Loading…</div>}
        {!loading && page.items.length === 0 && <div className="empty">Nothing in this queue.</div>}

        <div className="toolbar" style={{ padding: 'var(--s1) var(--s2)', justifyContent: 'space-between' }}>
          <span className="muted small">{page.totalItems} teacher(s)</span>
          <span style={{ display: 'flex', gap: 'var(--s0)' }}>
            <button type="button" className="btn btn-secondary btn-sm" disabled={page.page === 0}
                    onClick={() => load(page.page - 1)}>
              Previous
            </button>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!page.hasNext}
                    onClick={() => load(page.page + 1)}>
              Next
            </button>
          </span>
        </div>
      </section>
    </>
  );
}
